/* global removeTrapFocus */

const MainSearch = class extends HTMLElement {
  constructor() {
    super();

    // Clicking close button
    this.querySelectorAll('.main-search__close').forEach((el) => {
      el.addEventListener('click', (evt) => {
        evt.preventDefault();
        document.body.classList.remove('show-search');
        removeTrapFocus(theme.trapOpener);
      });
    });

    // Pressing escape key
    this.querySelector('.main-search__input').addEventListener('keyup', (evt) => {
      if (evt.key === 'Escape') {
        this.querySelector('.main-search__close').dispatchEvent(new Event('click'));
      }
    });

    // Search as you type
    const searchInput = this.querySelector('.main-search__input');
    searchInput.addEventListener('input', () => {
      this.classList.toggle('main-search--has-query', searchInput.value.trim().length > 0);
    });

    if (this.dataset.quickSearch === 'true') {
      this.initQuickSearch();
    }

    this.initRecentSearches();

    // Result navigation
    this.activeResultIndex = -1;
    this.querySelector('.main-search__input').addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  initRecentSearches() {
    this.recentSearchStorageKey = 'theme_recent_searches';
    this.recentSearchLimit = 5;
    this.recentSearchInput = this.querySelector('.main-search__input');
    this.recentSearchList = this.querySelector('[data-recent-search-list]');
    this.recentSearchContainer = this.querySelector('[data-recent-searches]');

    if (!this.recentSearchList || !this.recentSearchContainer) return;

    this.renderRecentSearches();

    const form = this.recentSearchInput.closest('form');
    if (form) {
      form.addEventListener('submit', () => {
        this.saveRecentSearch(this.recentSearchInput.value);
      });
    }

    this.addEventListener('click', (evt) => {
      const clearButton = evt.target.closest('[data-clear-recent-searches]');
      if (clearButton) {
        evt.preventDefault();
        this.clearRecentSearches();
        return;
      }

      const removeButton = evt.target.closest('[data-remove-recent-search]');
      if (removeButton) {
        evt.preventDefault();
        this.removeRecentSearch(removeButton.dataset.removeRecentSearch);
        return;
      }

      const searchLink = evt.target.closest('[data-search-term]');
      if (searchLink) this.saveRecentSearch(searchLink.dataset.searchTerm);
    });
  }

  getRecentSearches() {
    try {
      const searches = JSON.parse(window.localStorage.getItem(this.recentSearchStorageKey) || '[]');
      return Array.isArray(searches) ? searches.slice(0, this.recentSearchLimit) : [];
    } catch (error) {
      return [];
    }
  }

  setRecentSearches(searches) {
    try {
      window.localStorage.setItem(this.recentSearchStorageKey, JSON.stringify(searches.slice(0, this.recentSearchLimit)));
    } catch (error) {
      // Storage can be unavailable in private browsing; search still works.
    }
  }

  saveRecentSearch(term) {
    const cleanTerm = (term || '').trim();
    if (!cleanTerm) return;

    const searches = this.getRecentSearches().filter((search) => search.toLowerCase() !== cleanTerm.toLowerCase());
    searches.unshift(cleanTerm);
    this.setRecentSearches(searches);
    this.renderRecentSearches();
  }

  removeRecentSearch(term) {
    const searches = this.getRecentSearches().filter((search) => search !== term);
    this.setRecentSearches(searches);
    this.renderRecentSearches();
  }

  clearRecentSearches() {
    this.setRecentSearches([]);
    this.renderRecentSearches();
  }

  renderRecentSearches() {
    const searches = this.getRecentSearches();
    this.recentSearchList.innerHTML = '';
    this.recentSearchContainer.hidden = searches.length === 0;

    searches.forEach((term) => {
      const row = document.createElement('div');
      row.className = 'main-search__recent-item';

      const link = document.createElement('a');
      link.href = `${this.recentSearchInput.closest('form').action}?q=${encodeURIComponent(term)}`;
      link.textContent = term;

      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.setAttribute('aria-label', `Remove ${term} from recent searches`);
      removeButton.dataset.removeRecentSearch = term;
      removeButton.textContent = '×';

      row.appendChild(link);
      row.appendChild(removeButton);
      this.recentSearchList.appendChild(row);
    });
  }

  handleKeyDown(evt) {
    const options = [...this.querySelectorAll('.main-search__results [role="option"]')];

    const updateActiveOption = () => {
      options.forEach((opt) => opt.classList.remove('search-result-active'));
      const active = options[this.activeResultIndex];
      active.classList.add('search-result-active');
      evt.currentTarget.setAttribute('aria-activedescendant', active.id);
    };

    if (evt.key === 'ArrowDown') {
      evt.preventDefault();
      this.activeResultIndex = Math.min(this.activeResultIndex + 1, options.length - 1);
      updateActiveOption();
    }

    if (evt.key === 'ArrowUp') {
      evt.preventDefault();
      this.activeResultIndex = Math.max(this.activeResultIndex - 1, 0);
      updateActiveOption();
    }

    if (evt.key === 'Enter' && this.activeResultIndex >= 0) {
      evt.preventDefault();
      options[this.activeResultIndex].querySelector('a').click();
    }
  }

  initQuickSearch() {
    const searchInput = this.querySelector('.main-search__input');
    const searchTimeoutThrottle = 500;
    const resultLimit = 4;
    const includeMeta = this.dataset.quickSearchMeta === 'true';
    let searchTimeoutID = -1;
    let searchAbortController = null;
    let fullSearchAbortController = null;

    const showNoResults = (term, resultsBox) => {
      this.classList.remove('main-search--loading', 'main-search--has-full-results');
      this.classList.add('main-search--no-results');
      const noResultsTerm = this.querySelector('[data-no-results-term]');
      const noResultsMessage = this.querySelector('[data-no-results-message]');
      if (noResultsTerm) noResultsTerm.textContent = term;
      if (noResultsMessage) noResultsMessage.hidden = false;

      const emptyMessage = document.createElement('div');
      emptyMessage.className = 'main-search__empty-message';
      emptyMessage.innerHTML = theme.strings.generalSearchNoResultsWithoutTerms;
      resultsBox.innerHTML = '';
      resultsBox.appendChild(emptyMessage);
      searchInput.setAttribute('aria-expanded', false);
    };

    const fetchFullSearchFallback = (term, linkURL, resultsBox) => {
      if (fullSearchAbortController !== null) {
        fullSearchAbortController.abort('Existing request not needed');
      }
      fullSearchAbortController = new AbortController();

      const fullSearchURL = new URL(linkURL);
      fullSearchURL.searchParams.set('q', term);
      fullSearchURL.searchParams.set('section_id', 'main-search');

      fetch(fullSearchURL, { method: 'get', signal: fullSearchAbortController.signal })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.text();
        })
        .then((response) => {
          fullSearchAbortController = null;
          if (this.oldSearchValue !== term) return;

          const template = document.createElement('template');
          template.innerHTML = response;
          const fullResults = template.content.querySelector('.section-search-template .product-grid');
          const hasFullResults = !!fullResults && !!fullResults.querySelector('.product-block');

          if (!hasFullResults) {
            showNoResults(term, resultsBox);
            return;
          }

          this.classList.remove('main-search--loading', 'main-search--no-results');
          this.classList.add('main-search--has-full-results');
          resultsBox.innerHTML = '';

          const allStoreLink = document.createElement('a');
          allStoreLink.className = 'main-search__full-search-link';
          allStoreLink.href = linkURL;
          allStoreLink.append('Search the Entire Store for "');
          const allStoreTerm = document.createElement('strong');
          allStoreTerm.textContent = term;
          allStoreLink.appendChild(allStoreTerm);
          allStoreLink.append('"');
          resultsBox.appendChild(allStoreLink);
          searchInput.setAttribute('aria-expanded', true);
        })
        .catch((error) => {
          if (error.name === 'AbortError' || error.code === 20) return;
          console.warn(error); // eslint-disable-line no-console
          if (this.oldSearchValue === term) showNoResults(term, resultsBox);
        });
    };

    const handleInputChange = () => {
      const resultsBox = this.querySelector('.main-search__results');
      const valueToSearch = searchInput.value;
      /*
      const valueToSearch = window.theme.getCorrectedSearchTerm
        ? window.theme.getCorrectedSearchTerm(searchInput.value)
        : searchInput.value;
      */

      // Only search if search string longer than 2, and it has changed
      if (valueToSearch.length && valueToSearch !== this.oldSearchValue) {
        // Save previous value
        this.oldSearchValue = valueToSearch;

        // Kill outstanding ajax request
        if (searchAbortController !== null) {
          searchAbortController.abort('Existing request not needed');
          searchAbortController = null;
        }
        if (fullSearchAbortController !== null) {
          fullSearchAbortController.abort('Existing request not needed');
          fullSearchAbortController = null;
        }

        // Kill previous search
        clearTimeout(searchTimeoutID);

        // Create URL for full search results
        const form = searchInput.closest('form');
        const linkURL = new URL(form.action);
        const formParams = new URLSearchParams(new FormData(form));
        formParams.forEach((value, key) => { linkURL.searchParams.set(key, value); });

        // Show loading
        this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--no-results');
        this.classList.add('main-search--loading');
        if (!this.querySelector('.main-search__results-spinner')) {
          resultsBox.innerHTML = '<div class="main-search__results-spinner"><div class="loading-spinner"></div></div>';
        }

        // Do next search (in X milliseconds)
        searchTimeoutID = setTimeout(() => {
          searchAbortController = new AbortController();
          let ajaxUrl;

          if (theme.Shopify.features.predictiveSearch) {
            // use the API
            ajaxUrl = new URL(theme.routes.base + theme.routes.predictiveSearch);
            ajaxUrl.searchParams.set('q', valueToSearch);
            ajaxUrl.searchParams.set('section_id', 'predictive-search');
            ajaxUrl.searchParams.set('resources[type]', 'product');
            ajaxUrl.searchParams.set('resources[limit]', resultLimit);
            ajaxUrl.searchParams.set('resources[options][fields]', includeMeta ? 'title,product_type,variants.title,vendor,tag,variants.sku' : 'title,product_type,variants.title,vendor');
          } else {
            // use the theme template fallback
            ajaxUrl = new URL(linkURL);
            ajaxUrl.searchParams.set('section_id', 'main-search');
          }

          fetch(ajaxUrl, { method: 'get', signal: searchAbortController.signal })
            .then((response) => {
              if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
              }
              return response.text();
            })
            .then((response) => {
              const template = document.createElement('template');
              template.innerHTML = response;
              let resultsList = null;
              let resultsMessage = null;
              if (theme.Shopify.features.predictiveSearch) {
                resultsList = template.content.querySelector('.product-grid');
                resultsMessage = template.content.querySelector('[data-results-message]');
              } else {
                resultsList = template.content.querySelector('.section-search-template .product-grid');
                resultsMessage = template.content.querySelector('.section-search-template [data-results-message]');
              }

              // reset previous contents
              searchAbortController = null;
              this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--no-results');
              resultsBox.innerHTML = '';

              const resultsProducts = document.createElement('div');
              resultsProducts.className = 'main-search__results__products collection-listing';
              resultsProducts.innerHTML = '<div></div>';
              const resultsPages = document.createElement('div');
              resultsPages.className = 'main-search__results__pages';

              resultsProducts.firstElementChild.className = resultsList.className;

              // Convert standard blocks into quick search result format
              resultsList.querySelectorAll('.product-block').forEach((block, index) => {
                block.setAttribute('role', 'option');
                block.id = `SearchResult${index}`;
              });
              resultsList.querySelectorAll('.product-block:not(.collection-block):not(.page-block)').forEach((block, index) => {
                if (index < resultLimit) {
                  block.classList.add('main-search-result');
                  block.querySelectorAll('.btn.quickbuy-toggle').forEach((el) => el.remove());
                  block.querySelectorAll('.quickbuy-toggle').forEach((el) => el.classList.remove('quickbuy-toggle'));
                  resultsProducts.firstElementChild.appendChild(block);
                }
              });

              resultsList.querySelectorAll('.product-block.page-block').forEach((block) => {
                const item = document.createElement('a');
                item.className = 'main-search-result main-search-result--page';
                item.href = block.querySelector('a').href;
                item.innerHTML = '<div class="main-search-result__text"></div>';
                item.firstElementChild.innerText = block.querySelector('.page-block__title').innerText;
                resultsPages.appendChild(item);
              });

              this.classList.remove('main-search--loading');

              const areProducts = !!resultsProducts.querySelector('.main-search-result');
              const arePages = !!resultsPages.querySelector('.main-search-result');
              if (areProducts || arePages) {
                const status = document.querySelector('#HeaderSearchResultsStatus');
                if (status) status.innerHTML = resultsMessage.innerHTML;
                // Numerous results
                this.classList.add('main-search--has-results');
                this.classList.toggle('main-search--results-on-multiple-lines', resultsProducts.querySelectorAll('.product-block').length > 4);
                const allLink = document.createElement('a');
                allLink.className = 'main-search__results-all-link';
                allLink.href = linkURL;
                allLink.append('View All Results for "');
                const allLinkTerm = document.createElement('strong');
                allLinkTerm.textContent = searchInput.value.trim();
                allLink.appendChild(allLinkTerm);
                allLink.append('"');
                if (areProducts) {
                  const headingRow = document.createElement('div');
                  headingRow.className = 'main-search__results-heading-row';
                  const productsHeading = document.createElement('div');
                  productsHeading.className = 'main-search__results-heading h4 heading-font';
                  productsHeading.textContent = 'Recommended Products:';
                  headingRow.appendChild(productsHeading);
                  headingRow.appendChild(allLink);
                  resultsBox.appendChild(headingRow);
                  resultsBox.appendChild(resultsProducts);
                }
                if (arePages) {
                  const heading = document.createElement('h6');
                  heading.className = 'main-search-result__heading';
                  heading.innerHTML = theme.strings.generalSearchPages;
                  resultsPages.insertAdjacentElement('afterbegin', heading);
                  resultsBox.appendChild(resultsPages);
                }

                if (!areProducts) resultsBox.appendChild(allLink);
                searchInput.setAttribute('aria-expanded', true);
                this.activeResultIndex = -1;
              } else {
                if (theme.Shopify.features.predictiveSearch) {
                  fetchFullSearchFallback(valueToSearch, linkURL, resultsBox);
                } else {
                  showNoResults(valueToSearch, resultsBox);
                }
              }
            })
            .catch((error) => {
              console.warn(error); // eslint-disable-line no-console
            });
        }, searchTimeoutThrottle);
      } else if (!valueToSearch.length) {
        // Abandon current search
        this.oldSearchValue = valueToSearch;
        if (searchAbortController !== null) {
          searchAbortController.abort('Existing request not needed');
          searchAbortController = null;
        }
        if (fullSearchAbortController !== null) {
          fullSearchAbortController.abort('Existing request not needed');
          fullSearchAbortController = null;
        }
        clearTimeout(searchTimeoutID);

        // Clear results
        this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--loading', 'main-search--no-results', 'main-search--has-full-results');
        const noResultsMessage = this.querySelector('[data-no-results-message]');
        if (noResultsMessage) noResultsMessage.hidden = true;
        this.classList.remove('main-search--has-query');
        resultsBox.innerHTML = '';
        searchInput.setAttribute('aria-expanded', false);
      }
    };

    searchInput.addEventListener('keyup', handleInputChange.bind(this));
    searchInput.addEventListener('change', handleInputChange.bind(this));
  }
};

window.customElements.define('main-search', MainSearch);
